import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plane, Shield, Lock, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";

const LOGO = "https://customer-assets.emergentagent.com/job_2a175c3d-670a-4806-867b-48dd0b5b1184/artifacts/d7ah6vlh_cropped-3-1.png";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), pw);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden grid lg:grid-cols-2">
      {/* Left visual panel */}
      <div className="hidden lg:flex relative grid-bg-brand p-12 flex-col justify-between border-r border-[#1c1c1c]">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="Chongie Aviation" className="h-[76px] w-[76px]" data-testid="login-logo" />
          <div>
            <div className="font-heading text-lg font-bold tracking-tight uppercase">Chongie Aviation</div>
            <div className="label-eyebrow">Workforce Operations</div>
          </div>
        </div>

        <div className="max-w-xl">
          <h1 className="font-heading text-3xl xl:text-4xl font-bold tracking-tight leading-tight uppercase">
            Log time.<br />
            <span className="text-[#8a83d4]">Manage assignments.</span><br />
            Track project progress.
          </h1>
          <p className="mt-5 text-sm text-[#b9b2e8] leading-relaxed max-w-md">
            A central platform for engineers, mechanics, inspectors and project teams supporting aircraft maintenance, transitions and technical projects worldwide.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {[
            { k: "Live", v: "Clock-In" },
            { k: "Per-Project", v: "Reports" },
            { k: "Encrypted", v: "Documents" },
          ].map((b) => (
            <div key={b.k} className="border border-[#2a1f7a] bg-black/30 backdrop-blur-sm p-4">
              <div className="label-eyebrow" style={{ color: "#8a83d4" }}>{b.k}</div>
              <div className="font-heading text-xl font-semibold mt-1">{b.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="relative flex items-center justify-center p-8 lg:p-16 bg-brand-vertical">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <form onSubmit={submit} className="relative w-full max-w-md space-y-7" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <img src={LOGO} alt="Chongie" className="h-16 w-16" />
            <div className="font-heading font-bold uppercase tracking-tight">Chongie Aviation</div>
          </div>

          <div>
            <div className="label-eyebrow">SECURE ACCESS</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase mt-2">Sign in to console</h2>
            <p className="text-sm text-[#a1a1aa] mt-2">
              For authorised personnel only. All activity is logged.
            </p>
            <p className="text-xs text-[#8a83d4] mt-3 font-mono">
              First time? Sign in with your <span className="text-white">@chongieaviation.ie</span> email and the temporary password — you'll be prompted to set your own.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="label-eyebrow">EMAIL</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                  className="pl-10 bg-[#0a0a0a] border-[#222] rounded-none h-12 font-mono text-sm focus-visible:ring-1 focus-visible:ring-white focus-visible:ring-offset-0"
                  placeholder="you@chongieaviation.ie"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="pw" className="label-eyebrow">PASSWORD</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666]" />
                <Input
                  id="pw"
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                  data-testid="login-password"
                  className="pl-10 bg-[#0a0a0a] border-[#222] rounded-none h-12 font-mono text-sm focus-visible:ring-1 focus-visible:ring-white focus-visible:ring-offset-0"
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full h-12 rounded-none btn-brand font-heading font-bold uppercase tracking-[0.15em] text-sm border border-[#3b2bd0]"
          >
            {busy ? "Authenticating…" : "Engage →"}
          </Button>

          <div className="text-center">
            <a
              href="/forgot-password"
              data-testid="forgot-link"
              className="text-xs font-mono text-[#888] hover:text-white underline-offset-4 hover:underline"
            >
              Forgot your password?
            </a>
          </div>

          <div className="hairline" />

          <div className="flex items-center justify-between text-xs text-[#666] font-mono">
            <span className="flex items-center gap-2">
              <Shield className="h-3 w-3" /> JWT • bcrypt • audit logged
            </span>
            <span className="flex items-center gap-1">
              <Plane className="h-3 w-3" /> v1.0
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
